import React, {ReactNode} from 'react';

import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserTheme, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {SearchHintItemDropdown} from './searchHintItemDropdown';
import {renderHintDisplayedValueWithIcon, SearchHintItemPill} from './searchHintItemPill';
import {maybeTruncateValue, VisualSearchHint, VisualSearchHintValue} from './searchHintsHelpers';

/*
 * Props.
 */

interface SearchHintItemProps<T extends string = string> {
  hint: VisualSearchHint<T>;
  isDisabled: boolean;
  onClick: (hint: VisualSearchHint<T>, selectedValue: VisualSearchHintValue<T>) => void;
}

/*
 * Component.
 */

export const SearchHintItem = <T extends string = string>(props: SearchHintItemProps<T>) => {
  const {hint, isDisabled, onClick} = props;
  const {values, isEnabled, additionalIcon} = hint;

  const interactionId = `search-hint-${hint.type}`;

  const theme = useUserTheme();

  if (values.length === 1) {
    return (
      <SearchHintItemPill
        displayedValue={renderSearchHintDisplayedValue(hint, false, theme)}
        additionalIcon={additionalIcon}
        onClick={() => onClick(hint, values[0])}
        isActive={isEnabled}
        isDisabled={isDisabled}
        interactionId={interactionId}
      />
    );
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          displayedValue={renderSearchHintDisplayedValue(hint, isOpen, theme)}
          additionalIcon={additionalIcon}
          isActive={isEnabled}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        />
      )}
      renderModal={(popoverProps) => (
        <SearchHintItemDropdown
          {...popoverProps}
          hint={hint}
          onItemClick={(value: VisualSearchHintValue<T>) => onClick(hint, value)}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const renderSearchHintDisplayedValue = <T extends string = string>(
  hint: VisualSearchHint<T>,
  isHovered: boolean | undefined,
  theme: UserTheme,
): ReactNode => {
  // Use hint's title is defined.
  if (hint.title) {
    return maybeTruncateValue(hint.title, 30);
  }

  const {values, isEnabled, fallbackTitle} = hint;
  const selectedValues = values.filter(({isSelected}) => isSelected);

  // If hint is enabled, try to display the titles of the selected items, or their `modifier:value` pairs.
  if (isEnabled) {
    return selectedValues.flatMap(({title, icon, modifier, value, isSelected, key}, index) => [
      index > 0 ? <>,&nbsp;</> : null,
      renderHintDisplayedValueWithIcon(
        maybeTruncateValue(title || fallbackTitle || `${modifier}:${value}`),
        icon,
        isSelected,
        isHovered,
        theme,
        key,
      ),
    ]);
  }

  // Otherwise, fallback to hint's title.
  if (fallbackTitle) {
    return maybeTruncateValue(fallbackTitle);
  }

  // If fallbackTitle is not defined, try to use first `<modifier>:<value>`.
  if (hint.values.length > 0) {
    const {modifier, value} = hint.values[0];
    return maybeTruncateValue(`${modifier}:${value}`);
  }

  // This should not happen.
  throw new UnreachableError(hint as never);
};
